import { useMemo } from 'react'
import type { AppDispatch } from '@redux/store'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import * as templateGroupActions from '@redux/modules/templateGroup'

import type { EntityOptions } from '@redux/modules/types'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { TemplateGroupModel } from '@models/types'

const watchEntityKeys = ['templateGroups']

type LoadTemplateGroupIdsParams = {
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
  templateGroupIds: number[],
}

const loadTemplateGroupIds = (params: LoadTemplateGroupIdsParams) => {
  const { dispatch, requestOptions, templateGroupIds } = params
  const { loadTemplateGroupIds: loadFn } = templateGroupActions

  return dispatch(loadFn(templateGroupIds, requestOptions))
}

type TemplateGroupFilters = {
  brand_id?: number,
  string?: string,
}

type UseTemplateGroupsOptions = {
  filters?: TemplateGroupFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function useTemplateGroups(options: UseTemplateGroupsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    brand_id: filterBrandId,
    string: filterString,
  } = filters

  const {
    updatedEntities: { templateGroups: templateGroupsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const dispatch = useDispatch()

  const { templateGroups } = useSelector(reduxState => reduxState.entities)

  // Filter redux entities with provided filters
  const filteredTemplateGroups = useMemo(() => {
    const filtered = Object.values(templateGroups).filter((templateGroup: TemplateGroupModel) => {
      const { brand_id, id, title } = templateGroup

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchBrand = matchFilterNumber(brand_id, filterBrandId)

      return matchBrand && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [templateGroupsUpdatedAt, JSON.stringify(filters)])

  const filteredTemplateGroupsCount = filteredTemplateGroups.length
  const hasFilteredTemplateGroups = !!filteredTemplateGroupsCount

  // To be used in conjunction with DashboardModule.LoadMoreFooter
  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredTemplateGroupsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  // Load entities when performHttpRequests is true
  const reduxActionPayload = useReduxAction(
    'templateGroups',
    'loadTemplateGroups',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  const { loading: loadingTemplateGroups, reduxActionPerformed } = reduxActionPayload

  return {
    callbacks: {
      loadMore,
      loadTemplateGroupIds: (params: { templateGroupIds: number[], entityOptions?: EntityOptions }) => {
        const { templateGroupIds, entityOptions } = params
        return loadTemplateGroupIds({ dispatch, templateGroupIds, requestOptions: entityOptions })
      },
    },
    canLoadMore,
    filteredTemplateGroups,
    filteredTemplateGroupsCount,
    hasFilteredTemplateGroups,
    loading: loadingTemplateGroups,
    reduxActionPerformed,
    templateGroups,
  }
}

export default useTemplateGroups
